import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { TrendingUp, Plus, Printer, Eye, Edit, Trash2, Building } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Investment, Contract } from "@/types/contract";
import { useToast } from "@/hooks/use-toast";
import InvestmentForm from "../InvestmentForm";
import { DealSummary } from "../deals/DealSummary";
import { CorporateInvestorSummary } from "../deals/CorporateInvestorSummary";
import { InvestmentOpportunity } from "@/types/investment";

export function InvestmentsPage() {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingInvestment, setEditingInvestment] = useState<InvestmentOpportunity | null>(null);
  const [showDealSummary, setShowDealSummary] = useState(false);
  const [showCorporateSummary, setShowCorporateSummary] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const { toast } = useToast();

  const fetchInvestments = async () => {
    try {
      const { data, error } = await supabase
        .from('investments')
        .select(`
          *,
          contract:contracts(*)
        `);
      
      if (error) throw error;
      // Transform the data to match our Investment type
      const transformedData = (data || []).map(item => ({
        ...item,
        contract: item.contract as Contract
      })) as Investment[];
      setInvestments(transformedData);
    } catch (error) {
      console.error('Error fetching investments:', error);
      toast({
        title: "Error",
        description: "Failed to fetch investments",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, []);

  const handleEditInvestment = (investment: Investment) => {
    // Convert Investment to InvestmentOpportunity format for editing
    const investmentOpportunity: InvestmentOpportunity = {
      id: investment.id,
      title: investment.investment_title,
      description: investment.investment_description || '',
      status: investment.status,
      asking_price: investment.minimum_bid || 0,
      property_value: investment.contract?.property_value || 0,
      monthly_payment: investment.contract?.monthly_payment || 0,
      remaining_balance: investment.contract?.remaining_balance || 0,
      term_remaining: investment.contract?.term_remaining || '',
      collateral_details: investment.contract?.collateral_details || '',
      investor_yield: investment.contract?.investor_yield || '',
      investment_to_value: investment.contract?.investment_to_value || '',
      buyer_payment_history_summary: investment.contract?.buyer_payment_history_summary || '',
      buyer_credit_score_summary: investment.contract?.buyer_credit_score_summary || '',
      has_balloon_payment: investment.contract?.has_balloon_payment || false,
      balloon_payment_amount: investment.contract?.balloon_payment_amount,
      balloon_payment_due_date: investment.contract?.balloon_payment_due_date,
      property_address: investment.contract?.property_address,
      original_sales_price: investment.contract?.original_sales_price,
      original_down_payment: investment.contract?.original_down_payment,
      original_interest_rate: investment.contract?.original_interest_rate,
      original_monthly_payment: investment.contract?.original_monthly_payment,
      original_balloon_payment: investment.contract?.original_balloon_payment,
      realtor_com_link: investment.contract?.realtor_com_link,
      zillow_com_link: investment.contract?.zillow_com_link,
      county_assessor_link: investment.contract?.county_assessor_link,
      thumbnail_image: investment.contract?.thumbnail_image,
      documents_link: investment.contract?.documents_link,
    };
    setEditingInvestment(investmentOpportunity);
  };

  const handleDeleteInvestment = async (id: string) => {
    try {
      const { error } = await supabase
        .from('investments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Investment deleted successfully",
      });
      
      fetchInvestments();
    } catch (error) {
      console.error('Error deleting investment:', error);
      toast({
        title: "Error",
        description: "Failed to delete investment",
        variant: "destructive",
      });
    }
  };

  const handleSaveInvestment = async (investment: InvestmentOpportunity) => {
    try {
      // First, get the current investment to get the contract_id
      const { data: currentInvestment, error: fetchError } = await supabase
        .from('investments')
        .select('contract_id')
        .eq('id', investment.id)
        .single();

      if (fetchError) throw fetchError;

      // Update the investment record
      const { error: investmentError } = await supabase
        .from('investments')
        .update({
          investment_title: investment.title,
          investment_description: investment.description,
          status: investment.status,
          minimum_bid: investment.asking_price,
          calculated_yield: parseFloat(investment.investor_yield || '0'),
          calculated_ltv: investment.investment_to_value ? parseFloat(investment.investment_to_value.replace('%', '')) : null,
        })
        .eq('id', investment.id);

      if (investmentError) throw investmentError;

      // Update the related contract record
      const { error: contractError } = await supabase
        .from('contracts')
        .update({
          property_value: investment.property_value,
          monthly_payment: investment.monthly_payment,
          remaining_balance: investment.remaining_balance,
          term_remaining: investment.term_remaining,
          collateral_details: investment.collateral_details,
          investor_yield: investment.investor_yield,
          investment_to_value: investment.investment_to_value,
          buyer_payment_history_summary: investment.buyer_payment_history_summary,
          buyer_credit_score_summary: investment.buyer_credit_score_summary,
          has_balloon_payment: investment.has_balloon_payment,
          balloon_payment_amount: investment.balloon_payment_amount,
          balloon_payment_due_date: investment.balloon_payment_due_date,
          property_address: investment.property_address,
          original_sales_price: investment.original_sales_price,
          original_down_payment: investment.original_down_payment,
          original_interest_rate: investment.original_interest_rate,
          original_monthly_payment: investment.original_monthly_payment,
          original_balloon_payment: investment.original_balloon_payment,
          realtor_com_link: investment.realtor_com_link,
          zillow_com_link: investment.zillow_com_link,
          county_assessor_link: investment.county_assessor_link,
          thumbnail_image: investment.thumbnail_image,
          documents_link: investment.documents_link,
        })
        .eq('id', currentInvestment.contract_id);

      if (contractError) throw contractError;

      toast({
        title: "Success",
        description: "Investment updated successfully",
      });
      
      setEditingInvestment(null);
      fetchInvestments();
    } catch (error) {
      console.error('Error updating investment:', error);
      toast({
        title: "Error",
        description: "Failed to update investment",
        variant: "destructive",
      });
    }
  };

  const handlePrintSummary = (investment: Investment) => {
    if (investment.contract) {
      setSelectedContract(investment.contract);
      setShowDealSummary(true);
    }
  };

  const handlePrintCorporateSummary = (investment: Investment) => {
    if (investment.contract) {
      setSelectedContract(investment.contract);
      setShowCorporateSummary(true);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted animate-pulse rounded"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-muted animate-pulse rounded"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Investment Opportunities</h2>
          <p className="text-muted-foreground">Manage active investment opportunities</p>
        </div>
      </div>

      {investments.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Investment Opportunities</h3>
            <p className="text-muted-foreground mb-4">
              Promote contracts that are ready for investment to create opportunities.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {investments.map((investment) => (
            <Card key={investment.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{investment.investment_title}</CardTitle>
                    {investment.contract && (
                      <p className="text-sm text-muted-foreground">
                        {investment.contract.property_address || 'Property location not specified'}
                      </p>
                    )}
                  </div>
                  <Badge variant={investment.status === 'Available' ? 'default' : 'secondary'}>
                    {investment.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Minimum Bid</p>
                    <p className="font-semibold">
                      {investment.minimum_bid ? `$${investment.minimum_bid.toLocaleString()}` : 'Contact for details'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Expected Yield</p>
                    <p className="font-semibold">
                      {investment.calculated_yield ? `${investment.calculated_yield}%` : 'TBD'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">LTV Ratio</p>
                    <p className="font-semibold">
                      {investment.calculated_ltv ? `${investment.calculated_ltv}%` : 'TBD'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditInvestment(investment)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Investment</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this investment? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDeleteInvestment(investment.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handlePrintSummary(investment)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Deal Summary
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handlePrintCorporateSummary(investment)}
                    className="text-purple-600 hover:text-purple-700"
                  >
                    <Building className="h-4 w-4 mr-2" />
                    Corporate Summary
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Investment Dialog */}
      {editingInvestment && (
        <Dialog open={!!editingInvestment} onOpenChange={() => setEditingInvestment(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <InvestmentForm
              investment={editingInvestment}
              onSave={handleSaveInvestment}
              onCancel={() => setEditingInvestment(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Deal Summary Dialog */}
      <Dialog open={showDealSummary} onOpenChange={setShowDealSummary}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedContract && (
            <DealSummary 
              contract={selectedContract} 
              onClose={() => setShowDealSummary(false)} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Corporate Investor Summary Dialog */}
      <Dialog open={showCorporateSummary} onOpenChange={setShowCorporateSummary}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedContract && (
            <CorporateInvestorSummary 
              contract={selectedContract} 
              onClose={() => setShowCorporateSummary(false)} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}